import { FC, useCallback, useEffect, useState } from "react"
import { Formik, useFormikContext } from "formik"
import { WasmRunner } from "@coherentglobal/wasm-runner"
import { ResponseObject } from "@coherentglobal/wasm-runner/dist/types"

import { FormValues } from "../../types"
import { InputForm } from "./InputForm"
import { formatFormValues, formatRequestData, prepareFormValuesFromJson } from "../../utils/dataFormat"
import { InputJson } from "./InputJson"
import { isEqual, debounce } from "lodash"
import useFormValuesStore from "../../store/useFormValuesStore"

export type InputTabProps = {
  /**
   * Existing request data used to repopulate the form after switching tabs
   */
  request?: any
  /**
   * Existing response data used to display monthly payment
   */
  response?: any
  /**
   * Save the request so it can be used in the output tab
   */
  setRequest: (value: any) => void
  /**
   * Save the response so it can be used in the output tab
   */
  setResponse: (value: ResponseObject | { error: any }) => void
  /**
   * Function used to switch between tabs
   */
  setTabIndex: (value: number) => void
  /**
   * Wasm runner initializer function
   */
  initWasmRunner: () => void
  /**
   * Wasm runner
   */
  runner: WasmRunner
}

const SubmitListener: FC<{ runner: WasmRunner }> = ({ runner }) => {
  const formik = useFormikContext()
  const [lastValues, updateState] = useState(formik.values)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const submitForm = useCallback(
    debounce(
      (): void => {
        formik.submitForm()
      },
      500,
      { maxWait: 1500 }
    ),
    []
  )

  useEffect(() => {
    if (runner) {
      formik.submitForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runner])

  useEffect(() => {
    const valuesEqualLastValues = isEqual(lastValues, formik.values)
    const valuesEqualInitialValues = isEqual(formik.values, formik.initialValues)

    if (!valuesEqualLastValues) {
      updateState(formik.values)
    }

    if (!valuesEqualLastValues && !valuesEqualInitialValues && formik.isValid) {
      submitForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values, formik.isValid])

  return null
}

export const InputTab: FC<InputTabProps> = ({
  request = null,
  response = null,
  setRequest,
  setResponse,
  setTabIndex,
  initWasmRunner,
  runner,
}) => {
  const { setValues, values: storeValues } = useFormValuesStore((state) => state)
  const [jsonView, setJsonView] = useState<boolean>(false)
  const runCalculation = useCallback(
    async (payload: any) => {
      if (runner) {
        await runner
          .execute(payload)
          .then((data) => {
            setResponse(data)
          })
          .catch((err) => {
            return { error: err.v3 }
          })
      } else {
        await initWasmRunner()
      }
    },
    [initWasmRunner, runner, setResponse]
  )

  const calculate = useCallback(
    async (values: any) => {
      await runCalculation(formatRequestData(values))
      setRequest(values)
    },
    [runCalculation, setRequest]
  )

  const onSubmit = useCallback(
    (values: FormValues) => {
      setValues(values)
      const formattedValues = formatFormValues(values)
      calculate(formattedValues)
    },
    [calculate, setValues]
  )

  // workround, if there is no reponse data at first, send requests till u get response data
  useEffect(() => {
    let interval: NodeJS.Timer | undefined
    if (!response?.response_data?.outputs?.Bond_MV) {
      interval = setInterval(() => {
        onSubmit(storeValues)
      }, 1000)
    } else {
      if (interval) {
        clearInterval(interval)
      }
    }
    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [response, onSubmit, storeValues])

  const calculateJSON = async (values: any) => {
    const valuesForStore = prepareFormValuesFromJson(values)
    setValues(valuesForStore)
    const formattedValues = formatFormValues(valuesForStore)
    await runCalculation(formatRequestData(formattedValues))
    setRequest(values)
    setTabIndex(1)
  }

  return (
    <>
      <div className="h-24 w-full flex items-center justify-center">
        <h1 className="w-full max-w-xs text-center text-black text-3xl font-semibold">Bond Valuation Calculator</h1>
      </div>

      <div className="flex mx-auto w-full max-w-sm space-x-2">
        <button
          className={`${
            jsonView ? "bg-form-dark text-black" : "bg-brand-light text-white"
          } rounded-l py-2 px-8 text-sm w-1/2`}
          type="button"
          onClick={() => setJsonView(false)}
        >
          FIELDS
        </button>
        <button
          className={`${
            jsonView ? "bg-brand-light text-white" : "bg-form-dark  text-black"
          } rounded-r py-2 px-8 text-sm w-1/2`}
          type="button"
          onClick={() => setJsonView(true)}
        >
          JSON VIEW
        </button>
      </div>

      <Formik
        initialValues={storeValues}
        onSubmit={(values: FormValues) => {
          onSubmit(values)
        }}
      >
        {({ errors, touched, handleChange, setFieldValue }) =>
          jsonView ? (
            <InputJson calculate={calculateJSON} />
          ) : (
            <>
              <SubmitListener runner={runner} />
              <InputForm
                responseData={response ? response.response_data : null}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                setTabIndex={setTabIndex}
                onSubmit={onSubmit}
              />
            </>
          )
        }
      </Formik>
    </>
  )
}

